import React, { useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import floodDepthMeterImage from '../../../assets/floodDepthMeter.png';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useTranslation } from 'react-i18next';

const floodDepthLevels = [
  {
    label: '5m+',
    descriptionKey: 'overFiveMeters',
    color: '#6b39d3',
  },
  {
    label: '2〜5m',
    descriptionKey: 'twoToFiveMeters',
    color: '#de2525',
  },
  {
    label: '1〜2m',
    descriptionKey: 'oneToTwoMeters',
    color: '#ff7a1f',
    textColor: '#333',
  },
  {
    label: '0.5〜1m',
    descriptionKey: 'halfToOneMeter',
    color: '#ffd233',
    textColor: '#333',
  },
  {
    label: '0.1〜0.5m',
    descriptionKey: 'tenthToHalfMeter',
    color: '#f7f2e7',
    textColor: '#333',
  },
];

const imageWidth = 124;
const imageOverlapPadding = 88;

const FloodDepthMeter: React.FC = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isExpanded) {
    return (
      <Box
        sx={{
          minWidth: 100,
          borderRadius: 3,
          backgroundColor: 'rgba(38, 38, 38, 0.72)',
          boxShadow: '0 10px 24px rgba(0, 0, 0, 0.22)',
          px: 1.25,
          py: 1,
          backdropFilter: 'blur(4px)',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography sx={{ color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}>
            {t('common.label.depthOfFlooding')}
          </Typography>
          <IconButton
            onClick={() => setIsExpanded(true)}
            size="small"
            sx={{
              width: 24,
              height: 24,
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.8)',
              backgroundColor: 'rgba(255,255,255,0.08)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.16)',
              },
            }}
            aria-label={t('components.floodDepthMeter.openLegend')}
          >
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
        <Box
          sx={{
            mt: 1,
            borderRadius: 1.5,
            overflow: 'hidden',
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.12)',
          }}
        >
          {floodDepthLevels.map((level) => (
            <Box
              key={level.label}
              sx={{
                px: 1,
                py: 0.75,
                backgroundColor: level.color,
                color: level.textColor || '#fff',
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1,
                '& + &': {
                  borderTop: '1px solid rgba(255,255,255,0.22)',
                },
              }}
            >
              {level.label}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 300,
        borderRadius: 3,
        px: 1,
        py: 0.75,
        backdropFilter: 'blur(6px)',
        backgroundColor: 'rgba(0, 0, 0, 0.54)',
        boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
        <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 700, letterSpacing: '0.02em', pl: 0.25 }}>
          {t('common.label.depthOfFlooding')}
        </Typography>
        <IconButton
          onClick={() => setIsExpanded(false)}
          size="small"
          sx={{
            width: 22,
            height: 22,
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.72)',
            backgroundColor: 'rgba(255,255,255,0.08)',
            p: 0,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.16)',
            },
          }}
          aria-label={t('components.floodDepthMeter.closeLegend')}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: 194,
        }}
      >
        {floodDepthLevels.map((level, index) => (
          <Stack
            key={level.label}
            direction="row"
            alignItems="center"
              sx={{
                position: 'relative',
                minHeight: 38,
                backgroundColor: level.color,
                color: level.textColor || '#fff',
                borderBottom: index === floodDepthLevels.length - 1 ? 'none' : '4px solid #fff',
                '& + &': {
                  borderTop: '2px solid rgba(255,255,255,0.88)',
                },
              }}
          >
            <Box
              sx={{
                minWidth: 88,
                px: 1.25,
                py: 0.75,
                lineHeight: 1,
                position: 'relative',
                zIndex: 1,
                color: '#000',
                fontSize: '12px',
                fontWeight: 'bold',
                WebkitTextStroke: '0.2px #fff',
                textShadow: '-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff',
              }}
            >
              {level.label}
            </Box>
            <Box
              sx={{
                flex: 1,
                px: 1.25,
                py: 0.75,
                pr: `${imageOverlapPadding}px`,
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1.2,
                position: 'relative',
                zIndex: 5,
                color: index === floodDepthLevels.length - 1 ? '#2c2c2c' : '#fff',
                textShadow: index === floodDepthLevels.length - 1 ? 'none' : '0 1px 2px rgba(0,0,0,0.45)',
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
              }}
            >
              <Typography
                sx={{
                  color: '#000',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  WebkitTextStroke: '0.2px #fff',
                  textShadow: '-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff',
                }}
              >{t(`components.floodDepthMeter.${level.descriptionKey}`)}</Typography>
            </Box>
          </Stack>
        ))}
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: imageWidth,
            marginBottom: '3px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          <Box
            component="img"
            src={floodDepthMeterImage}
            alt={t('components.floodDepthMeter.referenceImage')}
            sx={{
              width: imageWidth,
              height: 'auto',
              display: 'block',
              flexShrink: 0,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default FloodDepthMeter;
